/*
  Mengenstaffel-Auswahl auf der Produktseite.

  - Klick auf eine Stufe setzt die Menge, die nötig ist, um sie zu ERREICHEN.
    Die Warenkorbmenge zählt mit (die Rabatt-Function summiert pro Produkt über
    alle Varianten), deshalb setzt "ab 25" bei 20 im Warenkorb die Menge auf 5.
  - Die Hervorhebung folgt der geltenden Stufe, nicht der günstigsten.
  - Die nächste Stufe zeigt, wie viele Stück noch fehlen.

  Grenzen kommen bewusst aus dem LEBENDEN Mengenfeld (min/max/step), nicht aus
  servergerenderten Attributen: Dawn zieht die bereits im Warenkorb liegende Menge
  vom Feld-Maximum ab und aktualisiert das Feld nach jedem Warenkorb-Update.
*/
if (!customElements.get('bp-staffel-picker')) {
  class BpStaffelPicker extends HTMLElement {
    connectedCallback() {
      if (this.bound) return; /* mehrfaches Einhängen darf nicht doppelt binden */
      this.bound = true;

      this.cartNote = this.querySelector('[data-cartnote]');
      this.announcer = this.querySelector('[data-announce]');
      /* Liegt ausserhalb des Pickers (direkt unter dem Mengenfeld) */
      this.unitPriceEl = document.querySelector(`#bp-staffel-${this.dataset.section}`)
        ? document.querySelector('[data-bp-unitprice]')
        : null;
      this.cartQty = parseInt(this.dataset.cartQty, 10) || 0;
      this.productId = this.dataset.productId;
      this.input = this.quantityInput();

      if (!this.input) {
        /* Ohne Mengenfeld (Block deaktiviert) sind die Karten reine Information —
           dann dürfen sie auch nicht wie Bedienelemente aussehen. */
        this.classList.add('bp-staffel--static');
        this.tiers.forEach((btn) => {
          btn.setAttribute('aria-disabled', 'true');
          btn.setAttribute('tabindex', '-1');
        });
        return;
      }

      /* Delegiert: ueberlebt einen Austausch der Kind-Elemente */
      this.onClick = (e) => {
        const btn = e.target.closest('.bp-staffel__tier');
        if (btn && this.contains(btn)) this.pick(btn);
      };
      this.addEventListener('click', this.onClick);

      this.onInput = () => this.refresh();
      this.input.addEventListener('change', this.onInput);
      this.input.addEventListener('input', this.onInput);

      /* Nach dem Hinzufügen im Drawer bleibt die Seite stehen — ohne dieses Abo
         würde weiter mit der alten Warenkorbmenge gerechnet. */
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.syncCart());
      }

      this.refresh();
    }

    /* Immer frisch abfragen: Bei einem innerHTML-Tausch (updateSourceFromDestination)
       ueberlebt das Element, seine Kinder werden aber ersetzt. */
    get tiers() {
      return Array.from(this.querySelectorAll('.bp-staffel__tier'));
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (this.input && this.onInput) {
        this.input.removeEventListener('change', this.onInput);
        this.input.removeEventListener('input', this.onInput);
      }
      this.unsubscribe?.();
      this.bound = false;
    }

    quantityInput() {
      const id = this.dataset.section;
      return (
        document.getElementById(`Quantity-${id}`) ||
        this.closest('product-info, .product')?.querySelector('.quantity__input') ||
        null
      );
    }

    /* Grenzen aus dem lebenden Feld — nie aus veralteten data-Attributen. */
    limits() {
      const min = parseInt(this.input.min, 10) || 1;
      const step = parseInt(this.input.step, 10) || 1;
      const rawMax = parseInt(this.input.max, 10);
      return { min, step, max: Number.isNaN(rawMax) ? null : rawMax };
    }

    /*
      Auf ein gültiges Vielfaches bringen. Erst aufrunden (nur so wird die Stufe
      erreicht), dann gegen das Maximum ABRUNDEN — ein gedeckelter Wert muss die
      Schrittweite erneut einhalten, sonst weist Shopify den Warenkorb ab.
      Rückgabe null = Stufe mit diesem Feld nicht erreichbar.
    */
    clamp(value) {
      const { min, step, max } = this.limits();
      let v = Math.max(value, min);
      const up = (v - min) % step;
      if (up !== 0) v += step - up;
      if (max !== null && v > max) {
        v = min + Math.floor((max - min) / step) * step;
        if (v > max || v < min) return null;
      }
      return v;
    }

    async syncCart() {
      if (!this.productId) return;
      try {
        const res = await fetch(`${window.routes?.cart_url || '/cart'}.js`);
        const cart = await res.json();
        this.cartQty = cart.items
          .filter((i) => String(i.product_id) === String(this.productId))
          .reduce((sum, i) => sum + i.quantity, 0);
        if (this.cartNote) {
          const tpl = (window.bpStaffelStrings || {}).inclCart || '';
          this.cartNote.textContent = tpl.replace('[qty]', this.cartQty);
          this.cartNote.hidden = this.cartQty === 0 || !tpl;
        }
      } catch (e) {
        /* Warenkorb nicht lesbar: lieber mit dem alten Wert weiterrechnen als abstürzen */
      }
      this.refresh();
    }

    pick(btn) {
      if (btn.getAttribute('aria-disabled') === 'true') return;
      const target = parseInt(btn.dataset.qty, 10);
      const needed = target - this.cartQty;
      /* Stufe liegt schon allein durch den Warenkorb an — dann die Eingabe des
         Nutzers NICHT überschreiben (sie wäre sonst auf das Minimum gefallen). */
      if (needed <= 0) {
        this.refresh();
        return;
      }
      const value = this.clamp(needed);
      if (value === null) return;
      this.input.value = value;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
      this.refresh(btn);
    }

    refresh(clicked) {
      if (!this.tiers.length || !this.input) return;
      const entered = parseInt(this.input.value, 10) || 0;
      const effective = entered + this.cartQty;
      const strings = window.bpStaffelStrings || {};

      let activeIdx = -1;
      this.tiers.forEach((btn, i) => {
        if (effective >= parseInt(btn.dataset.qty, 10)) activeIdx = i;
      });

      this.tiers.forEach((btn, i) => {
        const qty = parseInt(btn.dataset.qty, 10);
        const pct = btn.dataset.pct;
        const isActive = i === activeIdx;
        btn.classList.toggle('bp-staffel__tier--active', isActive);

        /* Erreichbarkeit prüfen: sonst führt der Klick ins Leere */
        const reachable = qty - this.cartQty <= 0 || this.clamp(qty - this.cartQty) !== null;
        btn.setAttribute('aria-disabled', reachable ? 'false' : 'true');
        btn.classList.toggle('bp-staffel__tier--unreachable', !reachable);

        const state = btn.querySelector('[data-state]');
        if (!state) return;
        let text = '';
        if (isActive) text = strings.active || '';
        else if (!reachable) text = strings.unreachable || '';
        else if (i === activeIdx + 1 && effective < qty) {
          text = (strings.missing || '').replace('[qty]', qty - effective).replace('[pct]', pct);
        }
        state.textContent = text;
        state.hidden = !text;
      });

      /* Geltender Stueckpreis unter dem Mengenfeld — nur wenn eine Stufe greift.
         Ohne Stufe stuende dort der Grundpreis, den der Preisblock schon zeigt. */
      if (this.unitPriceEl) {
        if (activeIdx >= 0 && strings.yourPrice) {
          const price = this.tiers[activeIdx].dataset.price || '';
          /* Der Locale-String enthaelt <strong> um den Preis — daher innerHTML.
             Die eingesetzten Werte kommen aus Liquid (escaped), nicht aus Nutzereingaben. */
          this.unitPriceEl.innerHTML = strings.yourPrice.replace('[price]', price);
          this.unitPriceEl.hidden = false;
        } else {
          this.unitPriceEl.textContent = '';
          this.unitPriceEl.hidden = true;
        }
      }

      /* Nur nach einem Klick ansagen — nicht bei jedem Tastendruck im Feld. */
      if (clicked && this.announcer && strings.announce) {
        const act = activeIdx >= 0 ? this.tiers[activeIdx].dataset.pct : '0';
        this.announcer.textContent = strings.announce
          .replace('[qty]', this.input.value)
          .replace('[pct]', act);
      }
    }
  }
  customElements.define('bp-staffel-picker', BpStaffelPicker);
}
